// File: src/pages/HR/SuddenApprovalCenter.js
/*
 * =====================================================================
 * WHAT CHANGED vs ORIGINAL:
 * -----------------------------------------------------------------------
 * ORIGINAL:
 *   - Fetched only status=PENDING records
 *   - No AM remark displayed on cards
 *   - No escalation flag shown
 *
 * NEW (ALL CHANGES MARKED // NEW):
 *   - fetchPendingRequests: filter includes PENDING AND previously
 *     escalated records (PENDING_MANAGER is not a status for sudden but
 *     records escalated from AM come back as PENDING — already correct)
 *   - Each card now shows approvalManagerRemark as VIEW ONLY section
 *     with a yellow badge if present — cluster manager can see why AM approved
 *   - Shows approvalManagerActedBy name on the card
 * =====================================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Grid, Button, Card, CardContent,
    Chip, Stack, Switch, FormControlLabel, Divider, CircularProgress,
    Alert, Tooltip, IconButton
} from '@mui/material';
import {
    AccessTime, CheckCircle, Cancel,
    InfoOutlined, TimerOff, Person
} from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const SuddenApprovalCenter = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [decisionMap, setDecisionMap] = useState({});

    const fetchPendingRequests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/sudden-requests', { params: { status: 'PENDING' } });
            // NEW: Accept PENDING (includes escalated from AM) and PENDING_MANAGER
            const pendingData = (res.data || []).filter(req =>
                req.status === 'PENDING' || req.status === 'PENDING_MANAGER'
            );
            setRequests(pendingData);

            const initialDecisions = {};
            pendingData.forEach(req => {
                const hour = req.createdAt ? new Date(req.createdAt).getHours() : 0;
                initialDecisions[req.id] = hour < 11;
            });
            setDecisionMap(initialDecisions);
        } catch (err) {
            toast.error('Failed to load pending requests');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPendingRequests(); }, [fetchPendingRequests]);

    const handleTogglePaid = (id, value) => {
        setDecisionMap(prev => ({ ...prev, [id]: value }));
    };

    const handleAction = async (id, status) => {
        setActionLoading(id);
        try {
            if (status === 'APPROVED') {
                const isPaid = !!decisionMap[id];
                await api.post(`/sudden-requests/${id}/approve`, null, {
                    params: { isPaid }
                });
                toast.success(isPaid ? 'Approved (Paid — No Deduction)' : 'Approved (Unpaid — Deduction Applied)');
            } else {
                const reason = window.prompt('Enter rejection reason:');
                if (reason === null) return;
                await api.post(`/sudden-requests/${id}/reject`, null, {
                    params: { reason }
                });
                toast.success('Request Rejected');
            }
            fetchPendingRequests();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Action failed');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" fontWeight={900} sx={{ color: '#1e293b' }}>
                        Sudden Request Queue
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Review emergency leaves and late permissions for your branch.
                    </Typography>
                </Box>
                <Chip label={`${requests.length} Pending`} color="primary" sx={{ fontWeight: 'bold' }} />
            </Stack>

            {requests.length === 0 ? (
                <Alert severity="success" variant="outlined" sx={{ borderRadius: 3 }}>
                    Great! All sudden requests have been processed.
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {requests.map((req) => {
                        const requestTime = req.createdAt ? new Date(req.createdAt) : new Date();
                        const isPostThreshold = requestTime.getHours() >= 11;

                        return (
                            <Grid item xs={12} md={6} key={req.id}>
                                <Card sx={{
                                    borderRadius: 4,
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                                    position: 'relative',
                                    overflow: 'visible'
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack direction="row" justifyContent="space-between" mb={2}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Person color="action" />
                                                <Typography variant="h6" fontWeight={800}>
                                                    {req.employeeName || req.employee?.name}
                                                </Typography>
                                            </Stack>
                                            <Chip
                                                label={req.requestType ? req.requestType.replace('_', ' ') : ''}
                                                size="small"
                                                sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 'bold' }}
                                            />
                                        </Stack>

                                        <Divider sx={{ my: 1.5 }} />

                                        <Stack spacing={1} mb={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccessTime sx={{ fontSize: 18, color: '#64748b' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Submitted: {req.createdAt ? format(requestTime, 'PPp') : '-'}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, minHeight: 60 }}>
                                                <strong>Reason:</strong> {req.reason}
                                            </Typography>
                                        </Stack>

                                        {/* NEW: Approval Manager remark VIEW ONLY section */}
                                        {req.approvalManagerRemark && (
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5, mb: 2, borderRadius: 2,
                                                    bgcolor: '#fefce8',
                                                    borderColor: '#fde047'
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                                                    <InfoOutlined sx={{ fontSize: 14, color: '#854d0e' }} />
                                                    <Typography fontSize={11} fontWeight={800} color="#854d0e">
                                                        APPROVAL MANAGER NOTE (View Only)
                                                    </Typography>
                                                </Stack>
                                                <Typography fontSize={12} color="#713f12">
                                                    "{req.approvalManagerRemark}"
                                                </Typography>
                                                {req.approvalManagerActedBy && (
                                                    <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.3 }}>
                                                        — {req.approvalManagerActedBy}
                                                    </Typography>
                                                )}
                                            </Paper>
                                        )}

                                        {isPostThreshold && (
                                            <Alert
                                                icon={<TimerOff fontSize="small" />}
                                                severity="warning"
                                                sx={{ py: 0, px: 1, mb: 2, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
                                            >
                                                Post-11AM Rule: Automatic half-day deduction unless marked Paid.
                                            </Alert>
                                        )}

                                        <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={!!decisionMap[req.id]}
                                                        onChange={(e) => handleTogglePaid(req.id, e.target.checked)}
                                                        color="success"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        {decisionMap[req.id] ? 'Paid (No Deduction)' : 'Unpaid (Deduct Salary)'}
                                                    </Typography>
                                                }
                                            />
                                            <Tooltip title="Toggle to decide if this emergency counts as a paid day or requires salary deduction.">
                                                <IconButton size="small"><InfoOutlined fontSize="small" /></IconButton>
                                            </Tooltip>
                                        </Box>

                                        <Stack direction="row" spacing={2} mt={3}>
                                            <Button
                                                fullWidth variant="contained" color="success"
                                                startIcon={actionLoading === req.id ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                                                onClick={() => handleAction(req.id, 'APPROVED')}
                                                disabled={!!actionLoading}
                                                sx={{ borderRadius: 2, fontWeight: 'bold' }}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                fullWidth variant="outlined" color="error"
                                                startIcon={<Cancel />}
                                                onClick={() => handleAction(req.id, 'REJECTED')}
                                                disabled={!!actionLoading}
                                                sx={{ borderRadius: 2, fontWeight: 'bold' }}
                                            >
                                                Reject
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

export default SuddenApprovalCenter;